import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Sparkles, KeyRound, Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword({ forced = false }) {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("New password must be at least 6 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    if (next === current) return toast.error("New password must be different from current");

    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password changed. Welcome to KARIS!");
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="w-full max-w-md p-8 rounded-2xl border border-border bg-card shadow-xl shadow-primary/5" data-testid="change-password-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-xl kn-gradient-bg flex items-center justify-center shadow-lg shadow-primary/30">
            <KeyRound className="w-5 h-5 text-white" />
          </div>
          <div className="leading-tight">
            <p className="font-display text-base font-semibold">KARIS</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Secure your account</p>
          </div>
        </div>

        <h1 className="font-display text-2xl font-semibold tracking-tight">
          {forced ? "Set a new password" : "Change password"}
        </h1>
        {forced && (
          <p className="text-sm text-muted-foreground mt-1">
            For security, you must replace the default password before continuing. Karibu, <span className="text-foreground font-medium">{user?.name || user?.email}</span>.
          </p>
        )}

        <form onSubmit={submit} className="mt-7 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em]">Current password</Label>
            <div className="relative">
              <Input
                data-testid="current-password-input"
                type={show ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                required
                autoComplete="current-password"
                className="h-11 pr-10"
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em]">New password</Label>
            <Input
              data-testid="new-password-input"
              type={show ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className="h-11"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.18em]">Confirm new password</Label>
            <Input
              data-testid="confirm-password-input"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
              className="h-11"
            />
          </div>

          <Button
            type="submit"
            data-testid="submit-change-password"
            disabled={loading}
            className="w-full h-11 kn-gradient-bg text-white border-0 hover:opacity-95 font-medium"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</> : "Update password"}
          </Button>

          {forced && (
            <button
              type="button"
              onClick={logout}
              className="block mx-auto text-xs text-muted-foreground hover:text-foreground"
              data-testid="forced-logout"
            >
              Sign out instead
            </button>
          )}

          {!forced && (
            <Button type="button" variant="ghost" className="w-full" onClick={() => navigate(-1)}>
              <Sparkles className="w-3 h-3 mr-1" /> Cancel
            </Button>
          )}
        </form>
      </Card>
    </div>
  );
}
